import { Theme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useEffect, useRef, useState } from 'react';
import Banner from '../Banner';
import { cleanSlideMetric, hhmmss } from '../Leaderboard';
import {
  AgentSlide,
  IPage,
  ISlideComponentProps,
  IUser,
  PageState,
  Slide,
  SlideEvent,
  SlideMetric,
  SlidePeriod,
  SlideType,
} from '../Slide';

const useStyles = makeStyles((theme: Theme) => ({
  agentSlideContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  leftContainer: {
    position: 'relative',
    marginRight: theme.spacing(4),
    minWidth: 500,
    height: 740,
    overflow: 'hidden',
  },
  rightContainer: {
    position: 'relative',
    flex: 1,
    height: 740,
    overflow: 'hidden',
  },
  topThreeContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    marginTop: theme.spacing(12),

    transition: '1000ms ease-out',
    opacity: 0,
  },
  topThreeContainerVisible: {
    opacity: 1,
  },
  topThreeContainerSlideLeft: {
    left: '-100%',
  },
  topThreeUser: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: 160,
  },
  topThreeFirstPlaceUser: {
    position: 'absolute',
    top: 170,
    left: 175,

    '& > div': {
      backgroundColor: '#CAE5D3',
    },
  },
  topThreeSecondPlaceUser: {
    position: 'absolute',
    top: 200,
    left: 18,

    '& > div': {
      backgroundColor: '#F6CBCC',
    },
  },
  topThreeThirdPlaceUser: {
    position: 'absolute',
    top: 230,
    left: 318,

    '& > div': {
      backgroundColor: '#92D2E9',
    },
  },
  topThreeUserIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    width: 60,
    height: 60,
    margin: 0,
    color: '#fff',
    fontSize: 24,
    fontWeight: 'normal',
  },
  topThreeUserName: {
    fontSize: 18,
    fontWeight: 'bold',
    margin: 0,
    padding: 0,
    textAlign: 'center',
  },
  topThreeStat: {
    fontSize: 16,
    fontWeight: 'bold',
    margin: 0,
    padding: 0,
    paddingLeft: 6,
    paddingRight: 6,
    color: '#4396C8',
    backgroundColor: '#DBEFF8',
    textAlign: 'center',
  },
  topThreeFirstPlaceCrown: {
    position: 'absolute',
    zIndex: 40,
    top: -30,
  },
  users: {
    display: 'flex',
    flexDirection: 'column',
    position: 'absolute',
    transition: 'top 1000ms ease-out',
    width: `calc(100% - ${theme.spacing(8)})`,
    borderCollapse: 'separate',
  },
  usersOffScreen: {
    transition: 'none',
    top: '100%',
  },
  usersScrollingIn: {
    top: 0,
  },
  usersVisible: {
    top: 0,
  },
  usersScrollingOut: {
    top: '-100%',
  },
  user: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    height: 66,
    border: '1px transparent solid',
    borderRadius: 5,
    backgroundColor: '#fff',
    marginBottom: theme.spacing(1),
    overflow: 'hidden',

    '& p': {
      margin: 0,
    },
  },
  userFirstPlace: {
    border: '1px #ffda00 solid',

    '& .userRank': {
      color: '#ffda00',
    },
    '& .userIcon': {
      backgroundColor: '#CAE5D3',
    },
  },
  userSecondPlace: {
    border: '1px #999999 solid',

    '& .userRank': {
      color: '#999999',
    },
    '& .userIcon': {
      backgroundColor: '#F6CBCC',
    },
  },
  userThirdPlace: {
    border: '1px #c98837 solid',

    '& .userRank': {
      color: '#c98837',
    },
    '& .userIcon': {
      backgroundColor: '#92D2E9',
    },
  },
  userRank: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#CBCBCB',
    marginLeft: theme.spacing(4),
    width: 30,
    textAlign: 'right',
  },
  userIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    width: 30,
    height: 30,
    color: '#fff',
    backgroundColor: '#CBCBCB',
    marginLeft: theme.spacing(3),
    marginRight: theme.spacing(3),
  },
  userName: {
    flex: 2,
    fontSize: 20,
    fontWeight: 'bold',
  },
  statContainer: {
    flex: 2,
    fontSize: 18,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  statLabel: {
    color: '#aaa',
    fontWeight: 'normal',
  },
  podium: {
    position: 'absolute',
    top: 300,
    left: 55,
  },
  belowPodium: {
    position: 'absolute',
    top: 448,
    left: 45,
    backgroundColor: '#f7f5f5',
    width: 420,
    height: 150,
  },
}));

const minutesToSeconds = (minutes) => minutes * 60;

export const MAX_AGENTS_PER_PAGE = 10;

const MIN_SECONDS_PER_PAGE = 3;
const MAX_SECONDS_PER_SLIDE = 5 * 60; // 5 minutes

export const getDurationMilliseconds = (slide: Slide, userPages: IPage[]) => {
  const result = getDurationSeconds(slide, userPages);
  return {
    slideDurationMs: Math.floor(
      Math.min(result.slideDurationInSec, MAX_SECONDS_PER_SLIDE) * 1000
    ),
    pageDurationMs: Math.floor(
      Math.max(result.pageDurationInSec, MIN_SECONDS_PER_PAGE) * 1000
    ),
  };
};

const isAgentSlide = (slide: Slide): boolean => slide.type === SlideType.agent;

const cleanSlideDuration = (slideDurationInSec: string | number): number => {
  if (typeof slideDurationInSec === 'number') {
    return slideDurationInSec;
  }

  switch (slideDurationInSec) {
    case '15seconds':
      return 15;
    case '30seconds':
      return 30;
    case '1minute':
      return 60;
    case '5minutes':
      return 5 * 60;
    default:
      throw new Error(`unsupported slideDurationInSec: ${slideDurationInSec}`);
  }
};

const getDurationSeconds = (slide: Slide, userPages: IPage[]) => {
  let { slideDurationInSec } = slide;

  slideDurationInSec = cleanSlideDuration(slideDurationInSec);

  // Use slideDuration for queue slide or any non-agent slide
  if (!isAgentSlide(slide)) {
    return {
      slideDurationInSec,
      pageDurationInSec: slideDurationInSec,
    };
  }

  // Use slideDuration as is without any modification since we only show the top 10 users and not rotate thru the rest
  if (!(slide as AgentSlide).showAllUsers) {
    return {
      slideDurationInSec,
      pageDurationInSec: slideDurationInSec,
    };
  }

  const totalAgentCount = (userPages ?? []).reduce(
    (acc, page) => acc + (page?.users?.length ?? 0),
    0
  );

  if (totalAgentCount === 0) {
    return {
      slideDurationInSec: MIN_SECONDS_PER_PAGE,
      pageDurationInSec: MIN_SECONDS_PER_PAGE,
    };
  }

  if (totalAgentCount > 500) {
    slideDurationInSec = minutesToSeconds(5);
  } else if (
    totalAgentCount > 100 &&
    (slideDurationInSec === 15 || slideDurationInSec === 30)
  ) {
    slideDurationInSec = minutesToSeconds(1);
  }

  const pageDurationInSec = slideDurationInSec / (userPages.length + 1);

  return {
    slideDurationInSec,
    pageDurationInSec,
  };
};

const mergePageState = (
  oldUserPages: IPage[],
  newUserPages: IPage[]
): IPage[] => {
  return newUserPages.map((newUserPage, index) => {
    const oldUserPage = oldUserPages[index];

    newUserPage.state = oldUserPage.state;

    return newUserPage;
  });
};

export default function Agent({
  slideObservable,
  leaderboard,
}: ISlideComponentProps) {
  const classes = useStyles();

  const slide = slideObservable.slide as AgentSlide;

  slide.metric = cleanSlideMetric(slide.metric);

  const [pages, setPages] = useState<IPage[]>(slide.pages);
  const [nextPages, setNextPages] = useState<IPage[]>();

  const pagesRef = useRef<IPage[]>();
  pagesRef.current = pages;

  const [currentPageIndex, setCurrentPageIndex] = useState(0);

  const [isTopThreeVisible, setIsTopThreeVisible] = useState(false);
  const [isPulseGlowVisible, setIsPulseGlowVisible] = useState(false);
  const [isUserListVisible, setIsUserListVisible] = useState(false);

  const rotate = () => {
    const pages = pagesRef.current ?? [];

    if (!isUserListVisible || !pages.length) return;

    const currentPage = pages[currentPageIndex];

    const pagesBefore = pages.slice(0, currentPageIndex);
    const pagesAfter = pages.slice(currentPageIndex + 1);

    const nextState = getNextState(currentPage);

    if (
      isSlideAnimatingOut ||
      (getNextCurrentPageIndex() === 0 &&
        nextState === PageState.scrollingout &&
        leaderboard.slideObservables.length > 1)
    ) {
      return;
    }

    const nextPage = getNextPage(pagesBefore, pagesAfter);

    if (nextPage) {
      nextPage.state = getNextPageState(nextState);
    }

    setPages([
      ...pagesBefore,
      { ...currentPage, state: nextState },
      ...pagesAfter,
    ]);

    if (nextState === PageState.scrollingout) {
      if (nextPages && getNextCurrentPageIndex() === 0) {
        setPages(mergePageState(pagesRef.current ?? [], nextPages));
        setNextPages(undefined);
      }
    } else if (nextState === PageState.offscreen) {
      setCurrentPageIndex(getNextCurrentPageIndex());
    }
  };

  const [isReady, setIsReady] = useState(false);

  const [isSlideAnimatingOut, setIsSlideAnimatingOut] = useState(false);

  useEffect(() => {
    const observer = (_slide: Slide, event: SlideEvent) => {
      setIsReady(true);

      if (event === SlideEvent.animateOutStarted) {
        setIsSlideAnimatingOut(true);
      }
    };

    slideObservable.observe(observer);

    return () => {
      slideObservable.unobserve(observer);
    };
  }, [slideObservable]);

  useEffect(() => {
    if (!isReady) return;

    const topThreeTimeout = setTimeout(() => {
      setIsTopThreeVisible(true);
    }, 50);

    const pulseGlowTimeout = setTimeout(() => {
      setIsPulseGlowVisible(true);
    }, 1000);

    const userListTimeout = setTimeout(() => {
      setIsUserListVisible(true);
    }, 250);

    return () => {
      clearTimeout(topThreeTimeout);
      clearTimeout(pulseGlowTimeout);
      clearTimeout(userListTimeout);
    };
  }, [isReady]);

  useEffect(() => {
    if (!isReady || !isUserListVisible) return;

    const rotateTimeout = setTimeout(rotate, 50);

    return () => {
      clearTimeout(rotateTimeout);
    };
  }, [isReady, isUserListVisible]);

  const getNextPage = (pagesBefore, pagesAfter) =>
    pagesAfter.length
      ? pagesAfter[0]
      : pagesBefore.length
      ? pagesBefore[0]
      : undefined;

  const getNextCurrentPageIndex = () =>
    currentPageIndex + 1 < pages.length ? currentPageIndex + 1 : 0;

  const getNextState = (page: IPage) => {
    switch (page.state) {
      case PageState.offscreen:
        return PageState.scrollingin;
      case PageState.scrollingin:
        return PageState.visible;
      case PageState.visible:
        return PageState.scrollingout;
      case PageState.scrollingout:
        return PageState.offscreen;
    }
  };

  const getNextPageState = (nextState: PageState) => {
    switch (nextState) {
      case PageState.offscreen:
        return PageState.visible;
      case PageState.scrollingin:
        return PageState.offscreen;
      case PageState.visible:
        return PageState.offscreen;
      case PageState.scrollingout:
        return PageState.scrollingin;
    }
  };

  const firstPage = pages?.[0]?.users ?? [];
  const firstPlaceUser = firstPage[0];
  const secondPlaceUser = firstPage[1];
  const thirdPlaceUser = firstPage[2];

  const getPageClassName = (page: IPage, isSlideAnimatingOut: boolean) => {
    if (isSlideAnimatingOut && page.state !== PageState.offscreen) {
      return classes.usersScrollingOut;
    }

    switch (page.state) {
      case PageState.offscreen:
        return classes.usersOffScreen;
      case PageState.scrollingin:
        return classes.usersScrollingIn;
      case PageState.visible:
        return classes.usersVisible;
      case PageState.scrollingout:
        return classes.usersScrollingOut;
    }
  };

  const getUserClassName = (user) => {
    switch (user.rank) {
      case 1:
        return classes.userFirstPlace;
      case 2:
        return classes.userSecondPlace;
      case 3:
        return classes.userThirdPlace;
      default:
        return '';
    }
  };

  const getTopThreeContainerClassName = (
    isTopThreeVisible,
    isSlideAnimatingOut
  ) => {
    if (isSlideAnimatingOut) {
      return classes.topThreeContainerSlideLeft;
    }

    return isTopThreeVisible ? classes.topThreeContainerVisible : '';
  };

  useEffect(() => {
    if (
      !slide.showAllUsers ||
      !slide.slideDurationInSec ||
      (pagesRef.current ?? []).length <= 1
    ) {
      return;
    }

    const duration = getDurationMilliseconds(slide, pagesRef.current ?? []);

    const interval = setInterval(rotate, duration.pageDurationMs / 2);

    return () => clearInterval(interval);
  }, [
    isUserListVisible,
    pages,
    currentPageIndex,
    nextPages,
    isReady,
    isSlideAnimatingOut,
    slideObservable,
  ]);

  const getSmallBannerText = () => {
    switch (slide.timePeriod) {
      case SlidePeriod.past14hours:
        return 'past 14 hours';
      case SlidePeriod.day:
      case SlidePeriod.past24hours:
        return "today's";
      case SlidePeriod.week:
      case SlidePeriod.last7days:
        return "this week's";
      case SlidePeriod.month:
      case SlidePeriod.last30days:
        return "this month's";
      case SlidePeriod.quarter:
        return "this quarter's";
      default:
        throw new Error(`unsupported period: ${slide.timePeriod}`);
    }
  };

  const getLargeBannerText = () => {
    switch (slide.metric) {
      case SlideMetric.allcalls:
        return 'Calls';
      case SlideMetric.allanswered:
        return 'Answered Calls';
      case SlideMetric.allconnections:
        return 'Connections';
      case SlideMetric.alldurationavg:
        return 'Avg. Duration';
      case SlideMetric.alldurationsum:
        return 'Duration';
      case SlideMetric.allincoming:
        return 'Incoming Calls';
      case SlideMetric.allmissed:
        return 'Missed Calls';
      case SlideMetric.alloutgoing:
        return 'Outgoing Calls';
      case SlideMetric.incominganswered:
        return 'Incoming Answered Calls';
      case SlideMetric.incomingconnection:
        return 'Incoming Connections';
      case SlideMetric.incomingmissed:
        return 'Incoming Missed Calls';
      case SlideMetric.outgoinganswered:
        return 'Outgoing Answered Calls';
      case SlideMetric.outgoingconnection:
        return 'Outgoing Connections';
      case SlideMetric.outgoingmissed:
        return 'Outgoing Missed Calls';
      case SlideMetric.tduration:
        return 'Total Duration';
      case SlideMetric.alldurationavgmin:
        return 'Avg. Duration';
      default:
        throw new Error(`unsupported metric: ${slide.metric}`);
    }
  };

  const getTopThreeStatValue = (user: IUser) => {
    switch (slide.metric) {
      case SlideMetric.allcalls:
        return user.allcalls;
      case SlideMetric.allanswered:
        return user.allanswered;
      case SlideMetric.allconnections:
        return user.allconnections;
      case SlideMetric.alldurationavg:
        return hhmmss(user.alldurationavg);
      case SlideMetric.alldurationsum:
        return hhmmss(user.alldurationsum);
      case SlideMetric.allincoming:
        return user.allincoming;
      case SlideMetric.allmissed:
        return user.allmissed;
      case SlideMetric.alloutgoing:
        return user.alloutgoing;
      case SlideMetric.incominganswered:
        return user.incominganswered;
      case SlideMetric.incomingconnection:
        return user.incomingconnection;
      case SlideMetric.incomingmissed:
        return user.incomingmissed;
      case SlideMetric.outgoinganswered:
        return user.outgoinganswered;
      case SlideMetric.outgoingconnection:
        return user.outgoingconnection;
      case SlideMetric.outgoingmissed:
        return user.outgoingmissed;
      case SlideMetric.tduration:
        return hhmmss(user.tduration);
      case SlideMetric.alldurationavgmin:
        return hhmmss(user.alldurationavg);
      default:
        throw new Error(`unsupported metric: ${slide.metric}`);
    }
  };

  const getTopThreeStatLabel = (user: IUser) => {
    switch (slide.metric) {
      case SlideMetric.allcalls:
        return getAllCallsLabel(user);
      case SlideMetric.allanswered:
        return getAllAnsweredLabel(user);
      case SlideMetric.allconnections:
        return getAllConnectionsLabel(user);
      case SlideMetric.alldurationavg:
        return getAllDurationAvgLabel(user);
      case SlideMetric.alldurationsum:
        return getAllDurationSumLabel(user);
      case SlideMetric.allincoming:
        return getAllIncomingLabel(user);
      case SlideMetric.allmissed:
        return getAllMissedLabel(user);
      case SlideMetric.alloutgoing:
        return getAllOutgoingLabel(user);
      case SlideMetric.incominganswered:
        return getIncomingAnsweredLabel(user);
      case SlideMetric.incomingconnection:
        return getIncomingConnectionLabel(user);
      case SlideMetric.incomingmissed:
        return getIncomingMissedLabel(user);
      case SlideMetric.outgoinganswered:
        return getOutgoingAnsweredLabel(user);
      case SlideMetric.outgoingconnection:
        return getOutgoingConnectionLabel(user);
      case SlideMetric.outgoingmissed:
        return getOutgoingMissedLabel(user);
      case SlideMetric.tduration:
        return getTotalDurationLabel(user);
      case SlideMetric.alldurationavgmin:
        return getAllDurationAvgMinLabel(user);
      default:
        throw new Error(`unsupported metric: ${slide.metric}`);
    }
  };

  const getTopThreeStatComponents = (user: IUser) => (
    <>
      {getTopThreeStatValue(user)} {getTopThreeStatLabel(user)}
    </>
  );

  return (
    <div className={classes.agentSlideContainer}>
      <div className={classes.leftContainer}>
        <Banner
          isReady={isReady}
          slideObservable={slideObservable}
          smallBannerText={getSmallBannerText()}
          largeBannerText={getLargeBannerText()}
        />
        {(firstPlaceUser || secondPlaceUser || thirdPlaceUser) && (
          <div
            className={`${
              classes.topThreeContainer
            } ${getTopThreeContainerClassName(
              isTopThreeVisible,
              isSlideAnimatingOut
            )}`}
          >
            {isPulseGlowVisible && (
              <div className="pulse-glow">
                <span></span>
              </div>
            )}
            <div className={classes.podium}>
              {isTopThreeVisible && (
                <object
                  type="image/svg+xml"
                  data="/media/leaderboard/animated-podium.svg"
                  width={400}
                />
              )}
            </div>
            <div className={classes.belowPodium} />
            {secondPlaceUser && (
              <div
                className={`${classes.topThreeUser} ${classes.topThreeSecondPlaceUser}`}
              >
                <div className={classes.topThreeUserIcon}>
                  {secondPlaceUser.icon}
                </div>
                <p className={classes.topThreeUserName}>
                  {secondPlaceUser.name}
                </p>
                <p className={classes.topThreeStat}>
                  {getTopThreeStatComponents(secondPlaceUser)}
                </p>
              </div>
            )}
            {firstPlaceUser && (
              <div
                className={`${classes.topThreeUser} ${classes.topThreeFirstPlaceUser}`}
              >
                <img
                  alt="crown"
                  src="/media/leaderboard/crown-badge.svg"
                  width={45}
                  className={classes.topThreeFirstPlaceCrown}
                />
                <div className={classes.topThreeUserIcon}>
                  {firstPlaceUser.icon}
                </div>
                <p className={classes.topThreeUserName}>
                  {firstPlaceUser.name}
                </p>
                <p className={classes.topThreeStat}>
                  {getTopThreeStatComponents(firstPlaceUser)}
                </p>
              </div>
            )}
            {thirdPlaceUser && (
              <div
                className={`${classes.topThreeUser} ${classes.topThreeThirdPlaceUser}`}
              >
                <div className={classes.topThreeUserIcon}>
                  {thirdPlaceUser.icon}
                </div>
                <p className={classes.topThreeUserName}>
                  {thirdPlaceUser.name}
                </p>
                <p className={classes.topThreeStat}>
                  {getTopThreeStatComponents(thirdPlaceUser)}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
      <div className={classes.rightContainer}>
        {isUserListVisible &&
          pages.map((page, pageIndex) => (
            <div
              className={`${classes.users} ${getPageClassName(
                page,
                isSlideAnimatingOut
              )}`}
              key={`page-${pageIndex}`}
            >
              {page.users.map((user, userIndex) => (
                <div
                  className={`${classes.user} ${getUserClassName(user)}`}
                  key={`user-${pageIndex}-${userIndex}`}
                >
                  <div className={`userRank ${classes.userRank}`}>
                    {user.rank}
                  </div>
                  <div className={`userIcon ${classes.userIcon}`}>
                    {user.icon}
                  </div>
                  <div className={classes.userName}>{user.name}</div>
                  {user.data.map((data) => (
                    <div key={data.title} className={classes.statContainer}>
                      <p>{data.value}</p>
                      <p className={classes.statLabel}>{data.title}</p>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ))}
      </div>
    </div>
  );
}

export const getAllCallsLabel = (user: IUser) =>
  user.allcalls === 1 ? 'Call' : 'Calls';

export const getAllAnsweredLabel = (user: IUser) =>
  user.allanswered === 1 ? 'Answered Call' : 'Answered Calls';

export const getAllConnectionsLabel = (user: IUser) =>
  user.allconnections === 1 ? 'Connection' : 'Connections';

export const getAllDurationAvgLabel = (_user: IUser) => 'Avg. Duration';

export const getAllDurationSumLabel = (_user: IUser) => 'Total Duration';

export const getAllIncomingLabel = (user: IUser) =>
  user.allincoming === 1 ? 'Incoming Call' : 'Incoming Calls';

export const getAllMissedLabel = (user: IUser) =>
  user.allmissed === 1 ? 'Missed Call' : 'Missed Calls';

export const getAllOutgoingLabel = (user: IUser) =>
  user.allmissed === 1 ? 'Outgoing Call' : 'Outgoing Calls';

export const getIncomingAnsweredLabel = (user: IUser) =>
  user.incominganswered === 1 ? 'Incoming Ans. Call' : 'Incoming Ans. Calls';

export const getIncomingConnectionLabel = (user: IUser) =>
  user.incomingconnection === 1
    ? 'Incoming Connection'
    : 'Incoming Connections';

export const getIncomingMissedLabel = (user: IUser) =>
  user.incomingmissed === 1 ? 'Incoming Missed Call' : 'Incoming Missed Calls';

export const getOutgoingAnsweredLabel = (user: IUser) =>
  user.outgoinganswered === 1 ? 'Outgoing Ans. Call' : 'Outgoing Ans. Calls';

export const getOutgoingConnectionLabel = (user: IUser) =>
  user.outgoingconnection === 1
    ? 'Outgoing Connection'
    : 'Outgoing Connections';

export const getOutgoingMissedLabel = (user: IUser) =>
  user.outgoingmissed === 1 ? 'Outgoing Missed Call' : 'Outgoing Missed Calls';

export const getTotalDurationLabel = (_user: IUser) => 'Total Duration';

export const getAllDurationAvgMinLabel = (_user: IUser) => 'Avg. Duration';
